import React, { useState, useEffect, useRef } from "react";
import { Edit, Trash2, Clock, Download, Paperclip, Eye } from "lucide-react";
import { TicketResposta } from "@/types";
import QuillEditor from "./QuillEditor";
import { processMentions } from "@/utils/mentionHelper";
import type ReactQuill from "react-quill";

interface TicketResponseProps {
  resposta: TicketResposta;
  usuario: any;
  isEditing: boolean;
  editingContent: string;
  onEdit: (resposta: TicketResposta) => void;
  onDelete: (resposta: TicketResposta) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onContentChange: (content: string) => void;
  onDownload: (anexo: any) => void;
  onView: (anexo: any) => void;
  canPreview: (anexo: any) => boolean;
  formatDate: (dateString: string) => string;
  renderUserInfo: (resposta: TicketResposta) => React.ReactNode;
  isFirstMessage?: boolean;
  allResponses?: TicketResposta[];
}

// Função para limpar HTML apenas na apresentação
const cleanHtmlForDisplay = (html: string): string => {
  if (!html) return html;

  let cleaned = html;

  // Remover tags <p><br></p> vazias (com ou sem espaços, com ou sem atributos, com ou sem self-closing)
  cleaned = cleaned.replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, "");

  // Remover múltiplos <p><br></p> consecutivos (incluindo com espaços entre eles)
  // Isso remove todos os <p><br></p> que aparecem juntos, mesmo com espaços
  while (cleaned.match(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi)) {
    cleaned = cleaned.replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, "");
  }

  // Remover parágrafos completamente vazios (sem conteúdo)
  cleaned = cleaned.replace(/<p[^>]*>\s*<\/p>/gi, "");

  // Remover múltiplos parágrafos vazios consecutivos no final
  cleaned = cleaned.replace(/(<p[^>]*>\s*<\/p>\s*)+$/gi, "");

  // Remover &nbsp; no final de parágrafos e tags (antes do fechamento)
  cleaned = cleaned.replace(/&nbsp;(\s*<\/p>)/gi, "$1");
  cleaned = cleaned.replace(/&nbsp;(\s*<\/span>)/gi, "$1");
  cleaned = cleaned.replace(/&nbsp;(\s*<\/div>)/gi, "$1");

  // Remover espaços em branco no final de tags (mas manter conteúdo)
  cleaned = cleaned.replace(/([^>])\s+<\/p>/gi, "$1</p>");
  cleaned = cleaned.replace(/([^>])\s+<\/span>/gi, "$1</span>");
  cleaned = cleaned.replace(/([^>])\s+<\/div>/gi, "$1</div>");

  // Remover tags vazias no final (spans, divs vazios)
  cleaned = cleaned.replace(/<span[^>]*>\s*<\/span>$/gi, "");
  cleaned = cleaned.replace(/<div[^>]*>\s*<\/div>$/gi, "");

  // Remover espaços no início e fim do HTML completo
  cleaned = cleaned.trim();

  // Se após a limpeza ficar vazio ou apenas tags vazias, retornar string vazia
  if (!cleaned || cleaned === "<p></p>" || cleaned === "<p><br></p>" || cleaned.match(/^<p[^>]*>\s*<\/p>$/gi)) {
    return "";
  }

  return cleaned;
};

export default function TicketResponse({
  resposta,
  usuario,
  isEditing,
  editingContent,
  onEdit,
  onDelete,
  onCancelEdit,
  onSaveEdit,
  onContentChange,
  onDownload,
  onView,
  canPreview,
  formatDate,
  renderUserInfo,
  isFirstMessage = false,
  allResponses = [],
}: TicketResponseProps) {
  // Limpar HTML apenas para apresentação
  const cleanedContent = cleanHtmlForDisplay(resposta.conteudo || "");
  
  // Processar menções no conteúdo
  const [processedContent, setProcessedContent] = useState(cleanedContent);
  
  useEffect(() => {
    if (cleanedContent) {
      processMentions(cleanedContent).then(processed => {
        setProcessedContent(processed);
      });
    } else {
      setProcessedContent(cleanedContent);
    }
  }, [cleanedContent]);

  // Mensagens do sistema não aparecem mais aqui (estão na timeline)
  // Este componente só renderiza mensagens de usuários reais

  const isOwner = resposta.usuario_id === usuario?.id;
  const isMessageEditing = isEditing;
  const editQuillRef = useRef<ReactQuill>(null);
  const editContentInitialized = useRef(false);

  // Ao entrar em modo de edição, preencher o Quill programaticamente (o ReactQuill não aplica value na montagem)
  useEffect(() => {
    if (!isMessageEditing) {
      editContentInitialized.current = false;
      return;
    }
    const contentToSet = editingContent || resposta.conteudo || "";
    if (!contentToSet.trim()) {
      editContentInitialized.current = false;
      return;
    }
    if (editContentInitialized.current) return;
    editContentInitialized.current = true;

    const applyContent = () => {
      if (!editQuillRef.current) return;
      const quill = editQuillRef.current.getEditor?.();
      if (!quill) return;
      quill.root.innerHTML = contentToSet;
      onContentChange(contentToSet);
    };

    // QuillEditor monta o ReactQuill após um tick ("Carregando editor..." -> ReactQuill); aplicar conteúdo quando estiver no DOM
    const t = setTimeout(applyContent, 100);
    return () => clearTimeout(t);
  }, [isMessageEditing, resposta.id, resposta.conteudo, editingContent, onContentChange]);

  // Verificar se a mensagem tem mais de 1 hora
  const isOlderThanOneHour = () => {
    if (!resposta.created_at) return false;
    const createdAt = new Date(resposta.created_at);
    const now = new Date();
    const diffMs = now.getTime() - createdAt.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours >= 1;
  };

  const canEdit = isOwner && !isOlderThanOneHour();
  const canDelete = isOwner && !isFirstMessage && !isOlderThanOneHour();

  return (
    <div className={`flex ${isOwner ? "justify-end" : "justify-start"} mb-3 sm:mb-4`}>
      <div
        className={`flex gap-2 sm:gap-3 max-w-[90%] sm:max-w-[85%] md:max-w-[75%] ${
          isOwner ? "flex-row-reverse" : "flex-row"
        }`}
      >
        {/* Avatar - sempre à esquerda (ou direita se for o dono) */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center overflow-hidden shadow-sm ring-2 ring-white">
            {resposta.usuario_avatar ? (
              <img
                src={`${import.meta.env.VITE_API_URL}/img/avatars/${
                  resposta.usuario_avatar
                }`}
                alt={`Avatar de ${resposta.usuario_nome}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-semibold text-xs sm:text-sm">
                {resposta.usuario_nome?.charAt(0)?.toUpperCase() || "U"}
              </span>
            )}
          </div>
        </div>

        {/* Conteúdo da mensagem */}
        <div className={`flex flex-col ${isOwner ? "items-end" : "items-start"} flex-1 min-w-0`}>
          {/* Nome e badges - apenas se não for o dono */}
          {!isOwner && (
            <div className="mb-1 sm:mb-1.5 px-0.5 sm:px-1">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-xs sm:text-sm">
                  {resposta.usuario_nome || "Usuário Desconhecido"}
                </h4>
                {renderUserInfo(resposta)}
              </div>
            </div>
          )}

          {/* Card da mensagem */}
          <div
            className={`relative rounded-xl sm:rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md ${
              isOwner
                ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-sm"
                : "bg-white dark:bg-gray-800 border border-blue-100 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-sm"
            } px-3 py-2 sm:px-4 sm:py-3 w-full`}
          >

            {/* Conteúdo da mensagem - modo de edição ou visualização */}
            {isMessageEditing ? (
              <div className="quill-edit-inline text-gray-900 dark:text-gray-100">
                <div className="quill-wrapper bg-white dark:bg-gray-800 rounded-lg">
                  <QuillEditor
                    ref={editQuillRef}
                    key={`edit-${resposta.id}`}
                    value={editingContent || resposta.conteudo || ""}
                    onChange={onContentChange}
                    className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg"
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ color: [] }, { background: [] }],
                        [{ align: [] }],
                        ["link", "image"],
                        ["clean"],
                      ],
                    }}
                    formats={[
                      "header",
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "list",
                      "bullet",
                      "color",
                      "background",
                      "align",
                      "link",
                      "image",
                    ]}
                    theme="snow"
                    placeholder="Digite sua resposta..."
                  />
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={onCancelEdit}
                    className="px-3 py-1.5 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-xs font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={onSaveEdit}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-200 text-xs font-medium shadow-sm"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`prose prose-sm sm:prose-base max-w-none leading-relaxed text-xs sm:text-sm ${
                  isOwner
                    ? "prose-invert prose-headings:text-white prose-p:text-white prose-strong:text-white prose-a:text-blue-100"
                    : "prose-gray dark:prose-invert prose-p:text-gray-900 dark:prose-p:text-gray-100 prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-gray-100"
                }`}
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            )}

            {/* Anexos */}
            {resposta.anexos && resposta.anexos.length > 0 && (
              <div
                className={`mt-2 sm:mt-3 pt-2 sm:pt-3 border-t ${
                  isOwner ? "border-blue-400/30" : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <h4
                  className={`text-[10px] sm:text-xs font-medium mb-1.5 sm:mb-2 flex items-center ${
                    isOwner ? "text-blue-100" : "text-gray-600 dark:text-gray-400"
                  }`}
                >
                  <Paperclip className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                  Anexos ({resposta.anexos.length})
                </h4>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {resposta.anexos.map((anexo) => (
                    <div
                      key={anexo.id}
                      className={`flex items-center gap-1.5 sm:gap-2 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 border ${
                        isOwner
                          ? "bg-blue-400/20 border-blue-300/30"
                          : "bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600"
                      }`}
                    >
                      <span
                        className={`text-[10px] sm:text-xs truncate flex-1 min-w-0 max-w-[100px] sm:max-w-none ${
                          isOwner ? "text-blue-50" : "text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {anexo.nome || "Anexo"}
                      </span>
                      <div className="flex gap-0.5 sm:gap-1">
                        {canPreview(anexo) && (
                          <button
                            onClick={() => onView(anexo)}
                            className={`p-1 sm:p-1.5 rounded transition-colors ${
                              isOwner
                                ? "text-blue-100 hover:bg-blue-400/30 hover:text-white"
                                : "text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300"
                            }`}
                            title="Visualizar"
                          >
                            <Eye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => onDownload(anexo)}
                          className={`p-1 sm:p-1.5 rounded transition-colors ${
                            isOwner
                              ? "text-blue-100 hover:bg-blue-400/30 hover:text-white"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-800 dark:hover:text-gray-200"
                          }`}
                          title="Download"
                        >
                          <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamp + ações */}
            <div
              className={`mt-2 sm:mt-2.5 flex items-center gap-1.5 sm:gap-2 flex-wrap ${
                isOwner ? "text-blue-100" : "text-gray-500 dark:text-gray-400"
              } text-[10px] sm:text-xs`}
            >
              <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
              <span className="truncate">{formatDate(resposta.created_at || "")}</span>
              {resposta.updated_at &&
                resposta.updated_at !== resposta.created_at && (
                  <span
                    className={`text-[9px] sm:text-[10px] ${
                      isOwner ? "text-blue-200/80" : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    (editado)
                  </span>
                )}
              {isOwner && !isMessageEditing && (
                <span className="inline-flex items-center gap-0.5 sm:gap-1 ml-1 sm:ml-2">
                  <button
                    onClick={() => onEdit(resposta)}
                    disabled={!canEdit}
                    className={`p-0.5 sm:p-1 rounded transition-all ${
                      canEdit
                        ? isOwner
                          ? "text-blue-100 hover:bg-blue-400/30 hover:text-white"
                          : "text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        : "text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed"
                    }`}
                    title={
                      !canEdit
                        ? isOlderThanOneHour()
                          ? "Mensagens com mais de 1 hora não podem ser editadas"
                          : "Você não pode editar esta mensagem"
                        : "Editar mensagem"
                    }
                  >
                    <Edit className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(resposta)}
                    disabled={!canDelete}
                    className={`p-0.5 sm:p-1 rounded transition-all ${
                      canDelete
                        ? isOwner
                          ? "text-blue-100 hover:bg-red-400/30 hover:text-white"
                          : "text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                        : "text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed"
                    }`}
                    title={
                      !canDelete
                        ? isFirstMessage
                          ? "A primeira mensagem não pode ser excluída, apenas editada"
                          : isOlderThanOneHour()
                          ? "Mensagens com mais de 1 hora não podem ser excluídas"
                          : "Você não pode excluir esta mensagem"
                        : "Excluir mensagem"
                    }
                  >
                    <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
